/**
 * PATH: packages/pricelist/src/pickedItem.ts
 * FILE_ID: PKG-PRICELIST-PICKED-001
 * PURPOSE: Resolve a picked grid row into a normalised PickedItem, applying the
 *          section's quote_field column mappings on top of the core defaults.
 * WHY: A section can declare which custom column feeds the quote line's
 *      sell / cost / productivity / unit (the "rules" mapping). Resolving it
 *      here keeps the Quote Builder host dumb — it just consumes PickedItem.
 * FLOW: GridRow + schema → apply quote_field map (else core fields) → PickedItem
 * RELATED: types.ts (PickedItem/ColumnDef), PricelistWorkbook.onPickItem
 */
import type { ColumnDef, PickedItem } from './types'
import type { GridRow } from './columns'

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Map a picked row → PickedItem. A column with quote_field='sell' (etc.)
 * overrides the default source for that quote field:
 *   sell         ← mapped column else computed_sell_price else base_cost
 *   cost         ← mapped column else base_cost
 *   productivity ← mapped column else productivity_pmpd
 *   unit         ← mapped column else row.unit
 */
export function resolvePickedItem(row: GridRow, schema: ColumnDef[]): PickedItem {
  const mapped: Record<string, unknown> = {}
  for (const col of schema) {
    if (col.quote_field) mapped[col.quote_field] = row[col.key]
  }
  const sell = toNum(mapped.sell) ?? toNum(row.computed_sell_price) ?? toNum(row.base_cost)
  const cost = toNum(mapped.cost) ?? toNum(row.base_cost)
  const productivity = toNum(mapped.productivity) ?? toNum(row.productivity_pmpd)
  const unitFromMap = typeof mapped.unit === 'string' && mapped.unit ? mapped.unit : null
  const unit = unitFromMap ?? (typeof row.unit === 'string' ? row.unit : undefined)
  return {
    item_code: String(row.item_code ?? ''),
    item_name: String(row.item_name ?? ''),
    description: typeof row.description === 'string' ? row.description : undefined,
    unit,
    cost,
    sell,
    productivity,
  }
}
